import type { ReactNode } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { FileIdentifier } from '@atlaskit/media-client';

import type { FieldId, OptionId } from '../index';

/** A string in ISO 8601 date format. */
export type IsoDate = string;

/** A string in ISO 8601 time format with no timezone. */
export type LocalTime = string;

export interface FormFile {
	creationDate: number;
	identifier: FileIdentifier;
	name: string;
	size: number;
	type: string;
}

/**
 * An option that has been selected.
 *
 * When this is a cascading option the label will be hyphen-concatenated.
 */
export interface FormOptionValue {
	id: OptionId;
	label: string;
	avatar?: ReactNode;
	byline?: string;
}

/**
 * A field value can take many forms, with different field types having different requirements.
 */
export interface AdfValue {
	adf: DocNode;
}

export interface DateTimeValue {
	date?: IsoDate;
	localTime?: LocalTime;
}

export interface DateValue {
	date: IsoDate;
}

export interface FilesValue {
	files: FormFile[];
}

export interface LocalTimeValue {
	localTime: LocalTime;
}

export interface NumberValue {
	number: number;
}

export interface OptionsValue {
	options: FormOptionValue[];
	otherOptionText?: string;
}

export interface TextValue {
	text: string;
}

/**
 * Generic definition for the API
 */
export type FieldValue =
	| AdfValue
	| DateTimeValue
	| DateValue
	| FilesValue
	| LocalTimeValue
	| NumberValue
	| OptionsValue
	| TextValue;

export interface FieldValues {
	[fieldId: FieldId]: FieldValue;
}

export interface FieldValuesPartial {
	[fieldId: FieldId]: FieldValue | undefined;
}

// So we can support other status in the future like 'error uploading' if needed
// oxlint-disable-next-line eslint/no-redeclare
export const FileStatus = {
	UPLOADING: 'uploading',
	UPLOADED: 'uploaded',
} as const;

export type FileStatus = (typeof FileStatus)[keyof typeof FileStatus];

export interface ModifiedFilesValue extends FilesValue {
	fileStatus: FileStatus;
}

/**
 * `null` represents that a value initially existed for the field, but the field now has no value.
 */
export type ModifiedFieldValue = Exclude<FieldValue, FilesValue> | ModifiedFilesValue | null;
/**
 * A field ID that does not exist, represents that the field value has not been modified.
 */
export interface ModifiedFieldValues {
	[fieldId: FieldId]: ModifiedFieldValue;
}

export const OTHER_ID = '0';

export const EMPTY_ADF: DocNode = {
	version: 1,
	type: 'doc',
	content: [],
};
